import { ListView, ListViewColumnType, initListView, connectListView } from "../../UI/view/list-view";
import { formatDate } from "../../../services/format";
import { DataSource } from "../../UI/view/data-source";
import { getContainerSummaries, GetN2AndHeMetrics } from "../../../services/requests/trackingRequests";
import { FormItemType } from "../../UI/view/component-map";
import { connectView } from "../../UI/view/view";
import { FormView } from "../../UI/view/form-view";

// check if a reading is within 72 hours
const createLastReadingWithin72HoursValidation = (timestampField: string) => ({
  lastReadingwithin72Hours: {
    handler: (row: any) => {
      const time = new Date();
      time.setHours(time.getHours() - 72);
      const timestamp = row[timestampField] ? new Date(row[timestampField]) : null;
      return !(timestamp && (timestamp <= time));
    },
    color: 'red',
    message: (row: any) => `Reading Time of ${formatDate(row[timestampField])} is older than 72 hours`
  }
});


// Define your data source for the container tracking summary endpoint
export const containerTrackingSummaryDataSource = {
  request: getContainerSummaries,
  name: 'containerTrackingSummary',
  pk: 'containerLabel',
  entityRequest: GetN2AndHeMetrics,
  entityRequestArgs: ({ getValues, additionaSelector }, entityId) => {
    if (!entityId) {
      return null;
    }
    const vals = getValues();
    const days = vals.days ? -Math.abs(vals.days) : -90;
    return { containerLabel: entityId, days: Math.max(days, -365), rank: vals.rank };
  },
} as DataSource;

// Define the ListView columns, matching your summary endpoint's fields
export const containerTrackingSummaryList: ListView = {
  columns: [
    {
      type: ListViewColumnType.icon,
      id: 'bar-chart',
      excludeGlobalFilter: true,
      accessor: ({ invoiceState }) => 'bar-chart',
      customPopup: () => ContainerTrackingPopup,
      iconStyle: () => ({ cursor: "pointer" })
    },
    {
      accessor: 'containerLabel',
      Header: 'Container ID',
      defaultSort: true,
      type: ListViewColumnType.linkEdit,
      linkPath: ({ value, row }) => `/telemetry/editliquid/${row.original.containerLabel}`
    },
    {
      accessor: 'currentLocation',
      Header: 'Current Location',
    },
    {
      accessor: 'lastKnownLocation',
      Header: 'Last Known Location',
    },
    {
      accessor: 'trackingDestination',
      Header: 'Destination',
    },
    {
      accessor: 'hePressure',
      Header: 'He Pressure',
      tooltip: ({ hePressureTs }) => `Reading Time of ${formatDate(hePressureTs)}`,
      validation: createLastReadingWithin72HoursValidation('hePressureTs')
    },
    {
      accessor: 'heLevel',
      Header: 'He Level',
      tooltip: ({ heLevelTs }) => `Reading Time of ${formatDate(heLevelTs)}`,
      validation: createLastReadingWithin72HoursValidation('heLevelTs')
    },
    {
      accessor: 'n2Pressure',
      Header: 'N2 Pressure',
      tooltip: ({ n2PressureTs }) => `Reading Time of ${formatDate(n2PressureTs)}`,
      validation: createLastReadingWithin72HoursValidation('n2PressureTs')
    },
    {
      accessor: 'n2Level',
      Header: 'N2 Level',
      tooltip: ({ n2LevelTs }) => `Reading Time of ${formatDate(n2LevelTs)}`,
      validation: createLastReadingWithin72HoursValidation('n2LevelTs')
    },
    {
      accessor: 'batteryLevel',
      Header: 'Battery Level',
      tooltip: ({ batteryLevelTs }) => `Reading Time of ${formatDate(batteryLevelTs)}`,
      validation: createLastReadingWithin72HoursValidation('batteryLevelTs')
    },
    {
      accessor: 'ambientTemperature',
      Header: 'Ambient Temperature',
      tooltip: ({ ambientTemperatureTs }) => `Reading Time of ${formatDate(ambientTemperatureTs)}`,
      validation: createLastReadingWithin72HoursValidation('ambientTemperatureTs')
    },
  ]
};

// Create the connected ListView component
export const [ContainerTrackingSummaryList, ContainerTrackingSummaryListRepo] = connectListView(
  containerTrackingSummaryList,
  containerTrackingSummaryDataSource
);


export const containerTrackingPopup = {
  editMode: true,
  items: [
    {
      type: FormItemType.row,
      items: [
        {
          type: FormItemType.fieldset,
          label: ({ watch }) => `${watch('containerLabel')}`,
          items: [
            {
              type: FormItemType.chart,
              componentArgs: {
                enableExport: true,
                dataSelector: (state) => state.containerTrackingSummary?.current?.data?.data,
                series: [
                  {
                    name: 'He Level',
                    type: 'line',
                    x: 'readingTs',
                    y: 'heLevel'
                  },
                  {
                    name: 'He Pressure',
                    type: 'line',
                    x: 'readingTs',
                    y: 'hePressure'
                  },
                  {
                    name: 'N2 Level',
                    type: 'line',
                    x: 'readingTs',
                    y: 'n2Level'
                  },
                  {
                    name: 'N2 Pressure',
                    type: 'line',
                    x: 'readingTs',
                    y: 'n2Pressure'
                  },
                ]
              }
            },
          ]
        },
        {
          type: FormItemType.fieldset,
          label: ({ watch }) => `${watch('containerLabel')}`,
          items: [
            {
              type: FormItemType.chart,
              componentArgs: {
                enableExport: true,
                dataSelector: (state) => state.containerTrackingSummary?.current?.data?.data,
                series: [
                  {
                    name: 'Batter Level',
                    type: 'line',
                    x: 'readingTs',
                    y: 'batteryLevel'
                  },
                  {
                    name: 'Ambient Temperature',
                    type: 'line',
                    x: 'readingTs',
                    y: 'ambientTemperature'
                  },

                ]
              }
            },
          ]
        },
      ]
    },
  ]
} as FormView;

export const [ContainerTrackingPopup, ContainerTrackingPopupRepo] = connectView({ form: containerTrackingPopup, existingRepo: ContainerTrackingSummaryListRepo as any });